import type { FC, ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { MediaSelectorApi } from "./context";
import Styles from "@/assets/styles/Styles";
import MediaSelectorDrawerContent from "@/components/MediaSelector/MediaSelectorDrawerContent";
import type { PersistentDrawerProps } from "@/components/PersistentDrawer";
import PersistentDrawer from "@/components/PersistentDrawer";
import { MediaType } from "@/declarations/models/MediaType";

/**
 * A callback to run when the selection changes
 */
type SelectionHandler = (value: any) => void;

/**
 *
 */
export interface MediaSelectorProps extends Partial<Pick<PersistentDrawerProps, "position" | "width">> {
  children: ReactNode;
  open: boolean;
  setOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
}

/**
 * Definition of all data and operations available
 */
export interface MediaSelectorAPI {
  openMediaDrawer: (
    availableMediaTypes: Array<MediaType>,
    onSelectHandler: SelectionHandler,
    selectMultiple?: boolean,
    selectMultipleLive?: boolean,
    selectedItems?: string[],
    canExpandContent?: boolean
  ) => void;
  closeMediaDrawer: () => void;
  setSelectedItems: (ids: string[]) => void;
  isOpen: boolean;
}

/**
 *
 * @param open
 * @param setOpen
 * @param children
 * @param width
 * @param position
 * @param topOffset
 * @constructor
 */
export const MediaSelector: FC<MediaSelectorProps> = ({ open, setOpen, children, width, position }) => {
  const [availableMediaTypes, setAvailableMediaTypes] = useState<Array<MediaType>>([]);
  const selectionHandlerRef = useRef<SelectionHandler | null>(null);
  const [selectMultiple, setSelectMultiple] = useState<boolean>(false);
  const [selectMultipleLive, setSelectMultipleLive] = useState<boolean>(false);
  const [selectedList, setSelectedList] = useState<string[]>();
  const [canExpandContent, setCanExpandContent] = useState<boolean>(false);

  const openMediaDrawer = useCallback(
    (
      mediaTypes: Array<MediaType>,
      handler: SelectionHandler,
      allowMultipleSelection?: boolean,
      allowMultipleSelectionLive?: boolean,
      selectedMediaIds?: string[],
      allowContentExpansion?: boolean
    ) => {
      if (selectionHandlerRef.current) {
        console.error("MediaSelector is busy. Please finish the ongoing operation");
        return;
      }
      if (!handler) {
        console.warn("No handler available. Refusing to open MediaSelector");
        return;
      }
      if (!mediaTypes?.length) {
        console.error("No MediaTypes available. Refusing to open MediaSelector.");
        return;
      }
      setAvailableMediaTypes(mediaTypes);
      selectionHandlerRef.current = handler;
      setSelectMultiple(!!allowMultipleSelection);
      setSelectMultipleLive(!!allowMultipleSelectionLive);
      setCanExpandContent(!!allowContentExpansion);

      if (selectedMediaIds) {
        setSelectedList(selectedMediaIds);
      } else {
        setSelectedList([]);
      }

      setOpen(true);
    },
    [setOpen]
  );

  const closeMediaDrawer = useCallback(() => {
    selectionHandlerRef.current = null;
    setOpen(false);
  }, [setOpen]);

  const setSelectedItems = useCallback((ids: string[]) => {
    setSelectedList(ids);
  }, []);

  const mediaSelectorAPI = useMemo<MediaSelectorAPI>(
    () => ({
      openMediaDrawer,
      closeMediaDrawer,
      setSelectedItems,
      isOpen: open,
    }),
    [open, openMediaDrawer, closeMediaDrawer, setSelectedItems]
  );

  const handleSelectionChanged: SelectionHandler = useCallback((value) => {
    selectionHandlerRef.current?.(value);
  }, []);

  return (
    <MediaSelectorApi.Provider value={mediaSelectorAPI}>
      <PersistentDrawer
        open={open}
        position={position || "left"}
        width={width || Styles.Dimensions.MEDIA_SELECTOR_DRAWER_WIDTH}
        backgroundColor={Styles.Colors.WHITE}
        drawerContent={
          <MediaSelectorDrawerContent
            availableMediaTypes={availableMediaTypes}
            onSelect={handleSelectionChanged}
            onClose={closeMediaDrawer}
            drawerOpen={open}
            selectMultiple={selectMultiple}
            selectMultipleLive={selectMultipleLive}
            selectedList={selectedList}
            canExpandContent={canExpandContent}
          />
        }
      >
        {children}
      </PersistentDrawer>
    </MediaSelectorApi.Provider>
  );
};

export default MediaSelector;
